//* 휴대폰 6자리 인증의 공통 상태 머신 — 발송(재발송 쿨다운)·코드 만료 카운트다운·확인
import { useCallback, useEffect, useRef, useState } from 'react';

import { errorMessageOf } from '@shared/utils/serverErrorMessage.util';
import { secondsUntil } from '@shared/utils/timeFormat.util';

/**
 * LoginPhoneVerification(로그인 2단계)·MePhoneVerification(비밀번호 변경 본인확인)이 같은 흐름을
 * 쓰지만 API 가 다르다. API 호출과 결과 처리만 옵션으로 받고 타이머·상태는 여기서 한 번만 관리한다.
 *
 * - 발송 성공: verificationId 저장, 코드 입력 초기화, 만료 카운트다운 + 재발송 쿨다운 시작
 * - 코드 만료: verificationId 를 비우고(입력칸 걷힘) 재발송 안내문
 * - 확인 실패: 서버 안내문(errorMessageOf) 을 error 로. 컴포넌트가 onSendError/onConfirmError 로
 *   먼저 받아 true 를 돌려주면 기본 error 표시는 건너뛴다 (구서버 404, 소진된 인증번호 등)
 * - 언마운트 시 interval 정리
 */

export const CODE_EXPIRED_MESSAGE = '인증시간이 만료되었습니다. 인증번호를 다시 받아주세요.';
export const SEND_FAILED_MESSAGE = '인증번호 발송에 실패했습니다. 잠시 후 다시 시도해 주세요.';
export const CONFIRM_FAILED_MESSAGE = '인증에 실패했습니다. 인증번호를 확인해 주세요.';
export const CODE_LENGTH_MESSAGE = '인증번호 6자리를 입력해 주세요.';

const DEFAULT_RESEND_COOLDOWN_SEC = 30;
const CODE_LENGTH = 6;

export interface PhoneCodeSendResult {
  verificationId: string;
  /** 인터셉터(handleAxiosDates)가 Date 로 바꿔 놓을 수 있다 */
  expiresAt: Date | string;
  phoneMasked?: string;
}

export interface UsePhoneCodeVerificationOptions<T> {
  send: () => Promise<PhoneCodeSendResult>;
  confirm: (args: { verificationId: string; code: string }) => Promise<T>;
  onVerified: (result: T) => void;
  /** 표시용 초기값 — 발송 응답에 phoneMasked 가 있으면 그것으로 바뀐다 */
  initialPhoneMasked?: string;
  resendCooldownSec?: number;
  /** true 를 돌려주면 훅의 기본 error 표시를 건너뛴다 */
  onSendError?: (err: unknown, message: string) => boolean | void;
  /** true 를 돌려주면 훅의 기본 error 표시를 건너뛴다 */
  onConfirmError?: (err: unknown, message: string) => boolean | void;
}

export interface UsePhoneCodeVerificationResult {
  verificationId: string | null;
  code: string;
  setCode: (code: string) => void;
  sending: boolean;
  confirming: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  /** 재발송까지 남은 초 (0 이면 발송 가능) */
  cooldown: number;
  /** 인증번호 만료까지 남은 초 */
  codeSeconds: number;
  phoneMasked: string;
  handleSend: () => Promise<void>;
  handleConfirm: () => Promise<void>;
  /** 타이머·인증 상태를 발송 전으로 되돌린다 (error 는 그대로 두므로 필요하면 setError) */
  reset: () => void;
}

export function usePhoneCodeVerification<T>(
  options: UsePhoneCodeVerificationOptions<T>,
): UsePhoneCodeVerificationResult {
  const {
    initialPhoneMasked = '',
    resendCooldownSec = DEFAULT_RESEND_COOLDOWN_SEC,
  } = options;

  //* 콜백은 매 렌더 새로 넘어와도 최신 것을 쓰고, 핸들러 참조는 상태에만 묶인다
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [phoneMasked, setPhoneMasked] = useState(initialPhoneMasked);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [codeSeconds, setCodeSeconds] = useState(0);

  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const codeTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    if (codeTimer.current) clearInterval(codeTimer.current);
    cooldownTimer.current = null;
    codeTimer.current = null;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const startCooldown = useCallback(() => {
    setCooldown(resendCooldownSec);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (cooldownTimer.current) clearInterval(cooldownTimer.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [resendCooldownSec]);

  const startCodeTimer = useCallback((expiresAt: Date | string) => {
    setCodeSeconds(secondsUntil(expiresAt));
    if (codeTimer.current) clearInterval(codeTimer.current);
    codeTimer.current = setInterval(() => {
      setCodeSeconds((prev) => {
        if (prev <= 1) {
          if (codeTimer.current) clearInterval(codeTimer.current);
          setVerificationId(null);
          setCode('');
          setError(CODE_EXPIRED_MESSAGE);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setVerificationId(null);
    setCode('');
    setCodeSeconds(0);
    setCooldown(0);
  }, [clearTimers]);

  const handleSend = useCallback(async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    setError(null);
    try {
      const result = await optionsRef.current.send();
      setVerificationId(result.verificationId);
      if (result.phoneMasked) setPhoneMasked(result.phoneMasked);
      setCode('');
      startCodeTimer(result.expiresAt);
      startCooldown();
    } catch (err) {
      const message = errorMessageOf(err, SEND_FAILED_MESSAGE);
      if (optionsRef.current.onSendError?.(err, message) === true) return;
      setError(message);
    } finally {
      setSending(false);
    }
  }, [cooldown, sending, startCodeTimer, startCooldown]);

  const handleConfirm = useCallback(async () => {
    if (confirming || !verificationId) return;
    if (code.length !== CODE_LENGTH) {
      setError(CODE_LENGTH_MESSAGE);
      return;
    }
    setConfirming(true);
    setError(null);
    try {
      const result = await optionsRef.current.confirm({ verificationId, code });
      optionsRef.current.onVerified(result);
    } catch (err) {
      const message = errorMessageOf(err, CONFIRM_FAILED_MESSAGE);
      if (optionsRef.current.onConfirmError?.(err, message) === true) return;
      setError(message);
    } finally {
      setConfirming(false);
    }
  }, [code, confirming, verificationId]);

  return {
    verificationId,
    code,
    setCode,
    sending,
    confirming,
    error,
    setError,
    cooldown,
    codeSeconds,
    phoneMasked,
    handleSend,
    handleConfirm,
    reset,
  };
}
